import { Add } from "@mui/icons-material";
import { Fab, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlClientUser } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";
import UserBadge from "~/modules/user/components/UserBadge";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useClientUserDelete from "./api/useClientUserDelete";
import useClientUserGetAllWithAccessToClient from "./api/useClientUserGetAllWithAccessToClient";

export default function ClientUserListScreen() {
    const { t } = useTranslation("user");
    const { client } = useRequireCurrentClient();

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            clientId: client.id,
            q: debouncedQ || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [client, debouncedQ, queryState],
    );

    const { data, loading, error, totalRows } = useClientUserGetAllWithAccessToClient(params);

    const { deleteClientUser, loading: deleteLoading } = useClientUserDelete();
    const handleDelete = useCallback(
        (userId: string) => deleteClientUser({ clientId: client.id, userId }),
        [client.id, deleteClientUser],
    );

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "user",
                headerName: t("userListScreen.colName"),
                renderCell: (params: GridRenderCellParams<GqlClientUser>) => {
                    const user = params.row.user;
                    if (!user) return null;
                    return (
                        <UserBadge
                            names={[user.firstName, user.lastName]}
                            profilePicture={user.profilePictureMedia?.url}
                            showName={true}
                        />
                    );
                },
                flex: 1,
            },
            {
                field: "email",
                headerName: t("userListScreen.colEmail"),
                valueGetter: (_, row) => row.user?.email,
                flex: 1,
            },
            {
                field: "phone",
                headerName: t("userListScreen.colPhone"),
                valueGetter: (_, row) => row.user?.phone,
                flex: 1,
            },
            {
                field: "actions",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: "right",
                renderCell: (params: GridRenderCellParams<GqlClientUser>) => (
                    <GaiaDeleteButton onDelete={() => handleDelete(params.row.userId)} isLoading={deleteLoading} />
                ),
            },
        ],
        [t, deleteLoading, handleDelete],
    );

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("userListScreen.title")}
                actions={
                    <Tooltip title={t("userListScreen.tooltipNewUser")}>
                        <Fab component={Link} to="new" color="primary">
                            <Add />
                        </Fab>
                    </Tooltip>
                }
            >
                <SearchInput
                    label={t("userListScreen.filterSearch")}
                    value={queryState.q}
                    onChange={value => handleParameterChange("q", value)}
                />

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={totalRows || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
